import { useEffect } from "react";

/**
 * Re-fits an autoResize textarea once the web fonts finish loading (ASB-4029).
 *
 * PrimeReact's InputTextarea (10.6) sizes an autoResize field on mount, focus,
 * blur and typing -- never when its text re-wraps. On a cold load Roboto is still
 * downloading at mount, so the field is sized for the fallback font; when Roboto
 * arrives the text wraps a line longer and, with overflow hidden, that last line
 * is simply not shown until the admin clicks into the field.
 *
 * Pass the textarea's ref. Mirrors PrimeReact's own fit (clear, then scrollHeight)
 * so the next keystroke's resize agrees with it.
 */
export default function useRefitOnFontLoad(ref) {
	useEffect(() => {
		const fonts = typeof document !== "undefined" ? document.fonts : undefined;
		if (!fonts) return undefined;
		let live = true;
		const refit = () => {
			const el = ref.current;
			// Only a field autoResize has sized, and only while it is rendered: a field
			// under a display:none ancestor measures 0 and would collapse to 0px.
			if (!live || !el || !el.style.height || !el.getClientRects().length) return;
			el.style.height = "";
			el.style.height = `${el.scrollHeight}px`;
		};
		fonts.ready.then(refit);
		// A later face (another Roboto weight) can load after `ready` first resolves.
		fonts.addEventListener("loadingdone", refit);
		return () => {
			live = false;
			fonts.removeEventListener("loadingdone", refit);
		};
	}, [ref]);
}
